import React, { useState, useEffect, useMemo } from "react";
import { navLinks, contactData } from "../data/mock";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useEvents } from "../hooks/useEvents";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("#hero");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { events } = useEvents();

  const links = useMemo(() => {
    if (!events || events.length === 0) return navLinks;
    const idx = navLinks.findIndex((l) => l.href === "#experience");
    const insertAt = idx >= 0 ? idx : navLinks.length;
    const next = navLinks.slice();
    next.splice(insertAt, 0, { label: "Eventos", href: "#events" });
    return next;
  }, [events]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = links.map(link => link.href.replace("#", ""));
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) {
            setActiveSection(`#${sections[i]}`);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [links]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.getElementById(href.replace("#", ""));
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-teal-950/85 backdrop-blur-md shadow-lg shadow-teal-900/20 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#hero"
          onClick={(e) => handleNavClick(e, "#hero")}
          className="group flex items-center gap-2"
        >
          <span className="font-bebas text-2xl tracking-wider text-white group-hover:text-teal-300 transition-colors duration-300">
            AURIE CANO
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className={`relative px-4 py-2 text-sm font-nunito font-medium tracking-wide transition-colors duration-300 ${
                activeSection === link.href
                  ? "text-teal-300"
                  : "text-white/70 hover:text-white"
              }`}
            >
              {link.label}
              {activeSection === link.href && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-teal-300 rounded-full" />
              )}
            </a>
          ))}
          <a href={`tel:${contactData.whatsapp.replace(/\s/g, '')}`}>
            <Button
              className="ml-4 bg-teal-600 hover:bg-teal-500 text-white font-nunito font-bold text-sm px-5 py-2 rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/25"
            >
              Contactar
            </Button>
          </a>
        </div>

        {/* Mobile Menu */}
        <div className="lg:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-teal-950 border-teal-900 w-72">
              <div className="flex flex-col gap-2 mt-8">
                {links.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className={`px-4 py-3 rounded-lg font-nunito font-medium text-base transition-all duration-300 ${
                      activeSection === link.href
                        ? "text-teal-300 bg-teal-400/10"
                        : "text-white/70 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </a>
                ))}
                <a
                  href={`tel:${contactData.whatsapp.replace(/\s/g, '')}`}
                  className="mt-4"
                >
                  <Button className="w-full bg-teal-600 hover:bg-teal-500 text-white font-nunito font-bold rounded-full">
                    Contactar
                  </Button>
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
